// src/features/frighi/FrighiPanel.jsx
import React, { useMemo, useState } from "react";
import { useCryoList } from "./useCryoList";
import CryoCard from "./CryoCard";
import Page from "../../components/Page";

function parseNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : n;
}

function hasDevice(device) {
  return !!device?.device_id;
}

function hasReading(device) {
  return !(
    device?.temp_ultima === null ||
    device?.temp_ultima === undefined ||
    device?.temp_ultima === ""
  );
}

function isDeviceAlert(device) {
  const min = parseNum(device?.limite_min);
  const max = parseNum(device?.limite_max);
  const currentTemp = parseNum(device?.temp_ultima);

  const outOfRange =
    currentTemp != null &&
    ((min != null && currentTemp < min) ||
      (max != null && currentTemp > max));

  return outOfRange || Number(device?.alert_ultimo) === 1;
}

function ManualFrigoCard({ device }) {
  const temp = parseNum(device?.temp_ultima);
  const min = parseNum(device?.limite_min);
  const max = parseNum(device?.limite_max);
  const inAlert = isDeviceAlert(device);

  const statusLabel = !hasDevice(device)
    ? "Modalità manuale"
    : !hasReading(device)
      ? "Cryo assegnato · in attesa letture"
      : "Cryo disponibile";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-lg font-semibold text-slate-800">
            {device?.nome || "Frigorifero"}
          </div>

          <div className="mt-2 flex flex-wrap gap-2">
            {!hasDevice(device) ? (
              <>
                <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs text-amber-700">
                  Modalità manuale
                </span>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600">
                  QR code
                </span>
              </>
            ) : (
              <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs text-blue-700">
                Cryo assegnato
              </span>
            )}

            {inAlert && (
              <span className="rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs text-red-700">
                Allarme
              </span>
            )}
          </div>
        </div>

        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">
            Ultima temperatura
          </div>
          <div className={`text-2xl font-semibold ${inAlert ? "text-red-600" : "text-slate-800"}`}>
            {temp != null ? `${temp.toFixed(1)}°C` : "—"}
          </div>
        </div>
      </div>

      <div className="mt-4 text-sm text-slate-500">
        {statusLabel}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="text-xs uppercase tracking-wide text-slate-400">
            Limite minimo
          </div>
          <div className="mt-1 text-sm font-medium text-slate-700">
            {min != null ? `${min}°C` : "—"}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="text-xs uppercase tracking-wide text-slate-400">
            Limite massimo
          </div>
          <div className="mt-1 text-sm font-medium text-slate-700">
            {max != null ? `${max}°C` : "—"}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="text-xs uppercase tracking-wide text-slate-400">
            MAC Cryo
          </div>
          <div className="mt-1 break-all text-sm text-slate-700">
            {device?.mac || "Non assegnato"}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="text-xs uppercase tracking-wide text-slate-400">
            Ultima attività
          </div>
          <div className="mt-1 text-sm text-slate-700">
            {device?.last_seen || "Nessuna lettura disponibile"}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FrighiPanel() {
  const { data, loading, error, setData } = useCryoList(null, { refreshMs: 60000 });
  const [onlyAlerts, setOnlyAlerts] = useState(false);

  async function refresh() {
    setData([...(data || [])]);
  }

  const alertsCount = useMemo(() => {
    return (data || []).reduce((acc, d) => acc + (isDeviceAlert(d) ? 1 : 0), 0);
  }, [data]);

  const filtered = useMemo(() => {
    if (!onlyAlerts) return data || [];
    return (data || []).filter(isDeviceAlert);
  }, [data, onlyAlerts]);

  if (loading) {
    return (
      <Page title="Frighi" back>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-600 shadow-sm">
          Caricamento frigoriferi…
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Frighi" back>
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700 shadow-sm">
          Errore: {error}
        </div>
      </Page>
    );
  }

  return (
    <Page title="Frighi" back>
      {alertsCount > 0 && (
        <div className="mb-6 flex items-start justify-between gap-4 rounded-2xl border border-red-200 bg-red-50 p-5">
          <div>
            <div className="mb-1 flex items-center gap-2 text-xs text-red-500">
              <span className="rounded-full border border-red-200 bg-white/60 px-2 py-0.5">
                SYSTEM STATUS
              </span>
              <span>
                {new Date().toLocaleTimeString("it-IT", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>

            <div className="text-2xl font-semibold text-red-600">
              Attenzione: {alertsCount} allarmi rilevati
            </div>

            <div className="mt-1 text-sm text-red-500">
              Sono stati rilevati valori fuori dai limiti di sicurezza.
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-3 rounded-2xl border border-red-200 bg-white/60 px-4 py-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-red-200 bg-red-50">
              <span className="text-lg text-red-600">⚠</span>
            </div>
            <div className="text-right">
              <div className="text-xl font-semibold text-red-600">{alertsCount}</div>
              <div className="text-[10px] uppercase tracking-wide text-red-500">
                Criticità
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4">
        <div className="text-2xl font-semibold text-slate-800">
          Monitoraggio Frigoriferi
        </div>
        <div className="text-sm text-slate-500">
          Gestione temperature automatiche e manuali
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-500">
          Totale frigoriferi:{" "}
          <span className="font-semibold text-slate-700">{(data || []).length}</span>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => window.open("/api/frighi/report-azienda-pdf.php?days=1", "_blank")}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            PDF azienda 24h
          </button>

          <button
            type="button"
            onClick={() => window.open("/api/frighi/report-azienda-pdf.php?days=7", "_blank")}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            PDF azienda 7 giorni
          </button>

          <button
            type="button"
            onClick={() => setOnlyAlerts(false)}
            className={`rounded-xl px-4 py-2 text-sm font-medium shadow-sm ${!onlyAlerts
                ? "border border-slate-900 bg-slate-900 text-white"
                : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
          >
            Tutti
          </button>

          <button
            type="button"
            onClick={() => setOnlyAlerts(true)}
            className={`rounded-xl px-4 py-2 text-sm font-medium shadow-sm ${onlyAlerts
                ? "border border-red-600 bg-red-600 text-white"
                : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
          >
            Solo allarmi
          </button>
        </div>
      </div>

      {!filtered.length ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="text-lg font-semibold text-slate-800">
            Nessun frigorifero configurato
          </div>
          <div className="mt-2 text-sm text-slate-500">
            Per questa azienda non risultano ancora frigoriferi registrati.
          </div>
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((d) => (
            <CryoCard
              key={`frigo-${d.frigo_id || d.id}`}
              device={d}
              onUpdated={refresh}
            />
          ))}
        </div>
      )}
    </Page>
  );
}